import './header.scss';

import cn from 'classnames';
import React from 'react';
import { matchPath, PathMatch, useLocation } from 'react-router-dom';

import { getModifierKey } from '@/shared/utils/platform';

import { PairSearchDropdown } from 'features/pair-search-nav';
import { NewYearMode, useNewYearMood } from 'hooks/useNewYearMood';
import { useNewYearSeason } from 'hooks/useNewYearSeason';
import { HEADER_LEFT_ELEMENT } from 'shared/consts/dom';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedSelector } from 'store';
import { ConnectWalletButton } from 'ui/ConnectWalletButton/ConnectWalletButton';
import { WalletButton } from 'ui/WalletButton/WalletButton';

import { MoreActionsMenu } from '../MoreActionsMenu';
import { PageHeaderPiece } from '../PageHeaderPiece/PageHeaderPiece';

import { SearchIcon } from 'assets/icons';

export function Header() {
  const { pathname } = useLocation();
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);
  const isNewYearSeason = useNewYearSeason();
  const {
    isEnabled: isSnowEnabled,
    toggle: toggleSnow,
    mode: snowMode,
    setMode: setSnowMode,
  } = useNewYearMood(isAdmin, isNewYearSeason);
  const [isSnowDropdownOpen, setIsSnowDropdownOpen] = React.useState(false);

  // Helper to map mode to icon
  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'crazy':
        return '🤪';
      case 'santa':
        return '🎅🏿';
      default:
        return '❄️';
    }
  };

  const dexPathMatch: PathMatch<'projectId' | 'pairId'> | null = matchPath(
    ROUTE_PATHS.dexPair,
    pathname,
  );

  return (
    <header className="mm-header">
      <div className="mm-header__left" id={HEADER_LEFT_ELEMENT}>
        <PageHeaderPiece />
      </div>
      <div className="mm-header__right">
        {isAdmin && !!dexPathMatch && (
          <>
            <ConnectWalletButton />
            <WalletButton />
          </>
        )}
        {isAdmin && isNewYearSeason && (
          <div
            className="relative"
            onMouseEnter={() => setIsSnowDropdownOpen(true)}
            onMouseLeave={() => setIsSnowDropdownOpen(false)}
          >
            <button
              type="button"
              onClick={toggleSnow}
              className={cn(
                'flex h-[40px] w-[40px] items-center justify-center rounded-md border border-[#1f2535] bg-[rgba(var(--bg-primary))] text-lg transition-colors duration-150 hover:bg-[#20263a]',
                {
                  'bg-blue-500/10 text-blue-400 border-blue-500/50': isSnowEnabled,
                  'text-gray-400': !isSnowEnabled,
                },
              )}
              title={isSnowEnabled ? 'Disable snow' : 'Enable snow'}
            >
              {getModeIcon(snowMode)}
            </button>
            {isSnowDropdownOpen && (
              <div className="absolute top-full left-1/2 pt-2 -translate-x-1/2 w-[40px] flex flex-col items-center z-50">
                <div className="flex flex-col gap-1 w-full rounded-md border border-[#1f2535] bg-[rgba(var(--bg-primary))] p-1 shadow-lg">
                  {(['snowflake', 'crazy', 'santa'] as NewYearMode[])
                    .filter(m => m !== snowMode)
                    .map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => {
                          setSnowMode(m);
                          if (!isSnowEnabled) toggleSnow();
                          setIsSnowDropdownOpen(false);
                        }}
                        className="flex h-[32px] w-full items-center justify-center rounded hover:bg-[#20263a] text-lg transition-colors"
                        title={`Switch to ${m} mode`}
                      >
                        {getModeIcon(m)}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
        {isAdmin && (
          <PairSearchDropdown
            trigger={({ onClick, isOpen }) => (
              <button
                type="button"
                onClick={onClick}
                data-testid="pair-search-trigger"
                className={cn(
                  'flex items-center gap-2 rounded-md border border-[#1f2535] bg-[rgba(var(--bg-primary))] px-3 py-2 text-gray-1 transition-colors duration-150',
                  {
                    'bg-backdrop-hover text-light-gray-1 ring-1 ring-primary': isOpen,
                    'hover:bg-[#20263a]': !isOpen,
                  },
                )}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={`Search pairs (${getModifierKey()}+K)`}
                title={`Search pairs (${getModifierKey()}+K)`}
              >
                <SearchIcon color={isOpen ? '#f5f6fb' : '#a7aec2'} />
                <span className="text-xs font-medium text-gray-400">{getModifierKey()} + K</span>
              </button>
            )}
          />
        )}

        <MoreActionsMenu />
      </div>
    </header>
  );
}
